import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Clock, Users, TrendingUp, DollarSign, Factory, Percent, Building2, Briefcase, Store, Target, BarChart3, AlertTriangle } from "lucide-react";
import { PeriodSelector, KPICard, IndicatorRow } from "@/components/UIComponents";
import { TrendModal } from "@/components/TrendModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell
} from "recharts";
import { formatCurrency, formatPercent, formatNumber, MONTHS, CONFIG } from "@/lib/config";
import TARGETS_CONFIG, { getStatusColor } from "@/lib/targets";
import { ScoreFiabiliteChefs } from "@/components/ScoreFiabiliteChefs";
import { KpiCoutEmployeurMoyenPondere } from "@/pages/dashboard/KpiCoutEmployeurMoyenPondere";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { toast } from "sonner";
import api from "@/lib/api";

const API = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const DEPTS_PROD = [
  { key: "PRQC", label: "Peinture PRQC", color: "#059669" },
  { key: "LMDS", label: "LMDS", color: "#2563eb" },
  { key: "LOCATION", label: "Location", color: "#d97706" },
];

export const Production = () => {
  const { user } = useAuth();
  const { canDo } = usePermissions() || {};
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via matrice.
  //   - `production_avertir_cp` : bouton "Avertir" sur alertes rouges premières commandes.
  //   - `production_analyses_fiabilite` : section "Analyses — Fiabilité des équipes".
  const canAvertirCP        = !!(canDo && canDo('production_avertir_cp'));
  const canVoirAnalysesFiab = !!(canDo && canDo('production_analyses_fiabilite'));
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1);
  const [quarter, setQuarter] = useState(1);
  const [view, setView] = useState("mensuel");
  const [selectedDept, setSelectedDept] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);
  const [ratios, setRatios] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [equipes, setEquipes] = useState(null);
  
  // Modal de tendance
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);

  // Production réelle vs objectif
  const [productionReelle, setProductionReelle] = useState(null);
  const [objectifsProd, setObjectifsProd] = useState([]);
  const [productionAnnuelle, setProductionAnnuelle] = useState([]);
  
  // Surveillance premières commandes
  const [surveillance, setSurveillance] = useState(null);
  const [survFournisseur, setSurvFournisseur] = useState(null);
  const [coutMateriaux, setCoutMateriaux] = useState(null);
  
  // Charger les cibles depuis MongoDB
  const [targets, setTargets] = useState(TARGETS_CONFIG);
  useEffect(() => {
    axios.get(`${window.__API_URL__ || ""}/api/configuration/targets`).then(r => r.data)
      .then(d => { if (d.targets) setTargets(d.targets); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { getMonthsForPeriod, aggregateMonthlyData } = await import('@/lib/periodUtils');
        const periods = getMonthsForPeriod(view, year, month, quarter);
        
        const monthsData = await Promise.all(
          periods.map(p => api.getDashboard(p.year, p.month))
        );
        
        const dashboard = aggregateMonthlyData(monthsData);
        setDashboardData(dashboard);
        
        if (view === "mensuel") {
          const prevMonth = month === 1 ? 12 : month - 1;
          const prevYear = month === 1 ? year - 1 : year;
          
          const [ratiosData, prevRatios] = await Promise.all([
            api.getRatios(year, month),
            api.getRatios(prevYear, prevMonth)
          ]);
          setRatios(ratiosData);
          setPreviousData(prevRatios);

          // Fetch team/subcontractor stats
          try {
            const eq = await axios.get(`${API}/api/production/equipes/${year}/${month}`);
            if (eq.status === 200) setEquipes(eq.data);
          } catch (e) { console.error("Error:", e); }
        }
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month, quarter, view]);

  // Charger production réelle et objectifs
  useEffect(() => {
    if (view !== "mensuel") return;
    Promise.all([
      axios.get(`${API}/api/production/reelle/${year}/${month}`).then(r => r.status === 200 ? r.data : null),
      axios.get(`${API}/api/objectifs/${year}`).then(r => r.status === 200 ? r.data : []),
      axios.get(`${API}/api/production/reelle/annuelle/${year}`).then(r => r.status === 200 ? r.data : []),
    ]).then(([prod, objs, annuelle]) => {
      setProductionReelle(prod);
      setObjectifsProd(Array.isArray(objs) ? objs.filter(o => o.type_objectif === "production_mensuel") : []);
      setProductionAnnuelle(Array.isArray(annuelle) ? annuelle : []);
    }).catch(() => {});
  }, [year, month, view]);

  // Charger surveillance premières commandes + fournisseur
  useEffect(() => {
    axios.get(`${API}/api/crm/surveillance-premieres-commandes`).then(r => r.status === 200 ? r.data : null)
      .then(d => setSurveillance(d))
      .catch(() => {});
    // Coût moyen matériaux LMDS
    axios.get(`${API}/api/inventory/movements?limit=500&department=LMDS`).then(r => r.status === 200 ? r.data : null)
      .then(d => {
        if (d?.movements) {
          const mouvements = d.movements.filter(m => m.movement_type === 'out');
          const parSemaine = {};
          let totalAnnuel = 0;
          mouvements.forEach(m => {
            const sem = m.semaine_lmds || 0;
            const cout = (m.unit_cost || 0) * (m.quantity || 0);
            totalAnnuel += cout;
            if (sem > 0) {
              parSemaine[sem] = (parSemaine[sem] || 0) + cout;
            }
          });
          const nbSemaines = Object.keys(parSemaine).length || 1;
          setCoutMateriaux({
            total_annuel: totalAnnuel,
            moyenne_semaine: totalAnnuel / nbSemaines,
            nb_semaines: nbSemaines,
          });
        }
      })
      .catch(() => {});
    if (view === "mensuel") {
      axios.get(`${API}/api/crm/surveillance-fournisseur/${year}/${month}`).then(r => r.status === 200 ? r.data : null)
        .then(d => setSurvFournisseur(d))
        .catch(() => {});
    }
  }, [year, month, view]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  const production = ratios?.production || {};
  const prevProduction = previousData?.production || {};
  const globalKpi = dashboardData?.kpis?.GLOBAL || {};
  const revenus = globalKpi.revenus || {};
  const prodTargets = targets.production || {};

  // Calculs de production
  const totalHeures = production.heures_terrain + production.heures_admin;
  const nbEmployesPRQC = 8;
  const nbEmployesLMDS = 3;
  const revenuParEmployePRQC = dashboardData?.kpis?.PRQC?.revenus?.total / nbEmployesPRQC || 0;
  const revenuParEmployeLMDS = dashboardData?.kpis?.LMDS?.revenus?.total / nbEmployesLMDS || 0;
  const revenuParEmployeCombine = revenus.total / (nbEmployesPRQC + nbEmployesLMDS) || 0;

  return (
    <div data-testid="production-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-slate-900">Efficacité & Performance de Production</h1>
          <p className="text-slate-500 mt-1 text-sm">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-full sm:w-48 bg-white">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">📊 Tous départements</SelectItem>
              <SelectItem value="PRQC">🎨 {CONFIG.department_names.PRQC}</SelectItem>
              <SelectItem value="LMDS">🏪 {CONFIG.department_names.LMDS}</SelectItem>
              <SelectItem value="LOCATION">🚚 {CONFIG.department_names.LOCATION}</SelectItem>
            </SelectContent>
          </Select>
          <PeriodSelector 
            year={year} 
            setYear={setYear} 
            month={month} 
            setMonth={setMonth}
            view={view}
            setView={setView}
            quarter={quarter}
            setQuarter={setQuarter}
          />
        </div>
      </div>

      {/* Centre de contrôle — Premières commandes */}
      {surveillance && surveillance.bons?.length > 0 && (
        <Card data-testid="surveillance-premieres-commandes" className={surveillance.stats?.rouge > 0 ? "border-red-200" : surveillance.stats?.orange > 0 ? "border-amber-200" : "border-emerald-200"}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-600" />
                Centre de contrôle — Prévisions gallons
                {surveillance.stats.rouge > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{surveillance.stats.rouge} urgent(s)</span>}
                {surveillance.stats.orange > 0 && <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">{surveillance.stats.orange} attention</span>}
                {surveillance.stats.rouge === 0 && surveillance.stats.orange === 0 && <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">Tout est en ordre</span>}
              </CardTitle>
            </div>
            <CardDescription>Suivi des prévisions de gallons et des commandes par bon de travail</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {surveillance.bons.map(b => (
                <a key={b.bon_id} href={`/crm/projets/${b.dossier_id}?tab=bon_travail`} className={`flex items-center justify-between p-3 rounded-lg border text-sm block hover:shadow-md transition-all ${
                  b.niveau_alerte === 'rouge' ? 'bg-red-50 border-red-200' : b.niveau_alerte === 'orange' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
                }`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${b.niveau_alerte === 'rouge' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      <p className="font-medium text-slate-900 truncate">{b.client_nom}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      {b.charge_projet && <span>CP: {b.charge_projet}</span>}
                      <span>Signé: {b.date_signature || '—'}</span>
                      <span>Début: {b.date_debut || '—'}</span>
                      {b.jours_avant_debut !== null && <span className={`font-bold ${b.jours_avant_debut <= 7 ? 'text-red-600' : 'text-amber-600'}`}>Dans {b.jours_avant_debut}j</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    <div className="text-center">
                      <p className="text-xs text-slate-400">Codes</p>
                      <p className={`text-sm font-bold ${b.nb_codes_couleur >= b.nb_produits && b.nb_produits > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {b.nb_codes_couleur}/{b.nb_produits}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-blue-500">Prévisions</p>
                      <p className={`text-sm font-bold ${b.prevision_faite ? 'text-emerald-600' : 'text-red-600'}`}>
                        {b.nb_previsions}/{b.nb_produits}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-400">Commandé</p>
                      <p className={`text-sm font-bold ${b.premiere_commande_faite ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {b.premiere_commande_faite ? 'Oui' : '—'}
                      </p>
                    </div>
                    {canAvertirCP && b.niveau_alerte === 'rouge' && (
                      <button
                        data-testid={`btn-avertir-cp-${b.bon_id}`}
                        onClick={() => {
                          if (confirm(`Envoyer un avertissement à ${b.charge_projet || 'la CP'} pour le bon de ${b.client_nom} ?`)) {
                            toast.info("Fonctionnalité d'avertissement disponible via la fiche employé → Mesures disciplinaires");
                          }
                        }}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                        title="Proposer un avertissement"
                      >
                        Avertir
                      </button>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Surveillance fournisseur — Plateforme vs QB */}
      {survFournisseur && survFournisseur.ecart?.alerte && (
        <Card data-testid="surveillance-fournisseur" className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              Écart fournisseur — {MONTHS[month]} {year}
            </CardTitle>
            <CardDescription>Différence entre les commandes sur la plateforme et les paiements réels chez les fournisseurs de peinture (QuickBooks)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                <p className="text-xs text-blue-600 font-semibold uppercase">Commandes plateforme</p>
                <p className="text-xl font-bold text-blue-800 mt-1">{survFournisseur.plateforme.total.toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}</p>
                <p className="text-xs text-blue-500 mt-0.5">{survFournisseur.plateforme.nb_commandes} commande(s)</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-center">
                <p className="text-xs text-purple-600 font-semibold uppercase">Payé chez fournisseurs (QB)</p>
                <p className="text-xl font-bold text-purple-800 mt-1">{survFournisseur.quickbooks.total_materiaux.toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}</p>
              </div>
              <div className={`p-3 rounded-lg text-center ${survFournisseur.ecart.alerte ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                <p className={`text-xs font-semibold uppercase ${survFournisseur.ecart.alerte ? 'text-red-600' : 'text-emerald-600'}`}>Écart</p>
                <p className={`text-xl font-bold mt-1 ${survFournisseur.ecart.alerte ? 'text-red-700' : 'text-emerald-700'}`}>{survFournisseur.ecart.montant.toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}</p>
                <p className={`text-xs mt-0.5 ${survFournisseur.ecart.alerte ? 'text-red-500' : 'text-emerald-500'}`}>{survFournisseur.ecart.pourcentage}% des achats fournisseur</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coût moyen matériaux LMDS */}
      {coutMateriaux && coutMateriaux.total_annuel > 0 && (
        <Card data-testid="cout-materiaux-lmds">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Factory className="w-4 h-4 text-purple-600" />
              Matériaux LMDS — Coût moyen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
                <p className="text-xs text-purple-600 font-medium">Moyenne / semaine</p>
                <p className="text-lg font-bold text-purple-800">{formatCurrency(coutMateriaux.moyenne_semaine)}</p>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <p className="text-xs text-slate-500 font-medium">Total annuel</p>
                <p className="text-lg font-bold text-slate-800">{formatCurrency(coutMateriaux.total_annuel)}</p>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <p className="text-xs text-slate-500 font-medium">Semaines actives</p>
                <p className="text-lg font-bold text-slate-800">{coutMateriaux.nb_semaines}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="% Heures Terrain"
          value={formatPercent(production.pct_heures_terrain)}
          subtitle={`${formatNumber(production.heures_terrain)} heures terrain`}
          icon={Clock}
          color="emerald"
          targetConfig={prodTargets.pct_heures_terrain}
          previousValue={prevProduction.pct_heures_terrain}
          description={prodTargets.pct_heures_terrain?.description}
          kpiKey="pct_heures_terrain"
          onClick={() => {
            setSelectedKPI({title: "% Heures Terrain", kpiKey: "pct_heures_terrain", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Revenu / Heure"
          value={formatCurrency(production.revenu_par_heure)}
          subtitle="Terrain uniquement"
          icon={DollarSign}
          color="amber"
          targetConfig={prodTargets.revenu_par_heure}
          previousValue={prevProduction.revenu_par_heure}
          description={prodTargets.revenu_par_heure?.description}
          kpiKey="revenu_par_heure"
          onClick={() => {
            setSelectedKPI({title: "Revenu / Heure", kpiKey: "revenu_par_heure", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Efficacité MO"
          value={formatCurrency(production.efficacite_mo)}
          subtitle="Profit brut / heure terrain"
          icon={TrendingUp}
          color="purple"
          targetConfig={prodTargets.efficacite_mo}
          previousValue={prevProduction.efficacite_mo}
          description={prodTargets.efficacite_mo?.description}
          kpiKey="efficacite_mo"
          onClick={() => {
            setSelectedKPI({title: "Efficacité MO", kpiKey: "efficacite_mo", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Heures Admin"
          value={formatNumber(production.heures_admin)}
          subtitle={`${formatPercent(100 - production.pct_heures_terrain)} du total`}
          icon={Clock}
          color="blue"
          kpiKey="heures_admin"
          onClick={() => {
            setSelectedKPI({title: "Heures Admin", kpiKey: "heures_admin", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
      </div>

      {/* Section Production Réelle vs Objectif */}
      {view === "mensuel" && (
        <Card className="animate-fade-in delay-150 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-600" />
              Production réelle vs Objectif — {MONTHS[month]} {year}
            </CardTitle>
            <CardDescription>
              PRQC: prorata jours travaillés · LMDS: installations · Location: factures finales
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!productionReelle ? (
              <p className="text-sm text-gray-400 italic">Chargement des données de production...</p>
            ) : (
              <div className="space-y-3">
                {DEPTS_PROD.map(({ key, label, color }) => {
                  const reel = productionReelle[key] || 0;
                  const objMois = objectifsProd.filter(o => o.mois === month && o.departement === key).reduce((s, o) => s + o.valeur, 0);
                  const pct = objMois > 0 ? Math.min(100, (reel / objMois) * 100) : 0;
                  const barColor = pct >= 100 ? "#059669" : pct >= 75 ? "#d97706" : pct >= 50 ? "#f59e0b" : "#dc2626";
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700">{label}</span>
                        <span className="text-slate-500">{formatCurrency(reel)} / {objMois > 0 ? formatCurrency(objMois) : "—"}</span>
                      </div>
                      {objMois > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-3">
                            <div className="h-3 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                          </div>
                          <span className="text-xs font-bold w-12 text-right" style={{ color: barColor }}>{pct.toFixed(0)}%</span>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600">Aucun objectif de production configuré → Cibles et seuils → Objectifs Production</p>
                      )}
                    </div>
                  );
                })}
                <div className="mt-3 pt-3 border-t flex justify-between items-center">
                  <span className="text-sm font-semibold text-slate-700">Total production {MONTHS[month]}</span>
                  <span className="text-sm font-bold text-blue-700">{formatCurrency(productionReelle.total)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Chart Production mensuelle par département */}
      {view === "mensuel" && productionAnnuelle.length > 0 && (
        <Card className="animate-fade-in delay-200 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-slate-600" />
              Production mensuelle par département — {year}
            </CardTitle>
            <CardDescription>
              Barres empilées : PRQC (prorata jours) + LMDS (installations) + Location (factures finales)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="production-annuelle-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productionAnnuelle} barSize={28}>
                  <XAxis dataKey="mois_nom" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v === 0 ? '0' : `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value, name) => [formatCurrency(value), name === "PRQC" ? "Peinture PRQC" : name === "LMDS" ? "LMDS" : "Location"]}
                    labelFormatter={label => `${label} ${year}`}
                  />
                  <Legend formatter={name => name === "PRQC" ? "Peinture PRQC" : name === "LMDS" ? "LMDS" : "Location"} />
                  <Bar dataKey="PRQC" stackId="prod" fill="#059669" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="LMDS" stackId="prod" fill="#2563eb" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="LOCATION" stackId="prod" fill="#d97706" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Totaux par département */}
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t">
              {DEPTS_PROD.map(({ key, label, color }) => {
                const total = productionAnnuelle.reduce((s, m) => s + (m[key] || 0), 0);
                return (
                  <div key={key} className="text-center">
                    <div className="w-3 h-3 rounded mx-auto mb-1" style={{ backgroundColor: color }} />
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm font-bold" style={{ color }}>{formatCurrency(total)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Détails */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in delay-200">
        {/* Répartition des heures */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition des Heures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Heures Terrain</span>
                  <span className="font-medium">{formatNumber(production.heures_terrain)} hrs ({formatPercent(production.pct_heures_terrain)})</span>
                </div>
                <Progress value={production.pct_heures_terrain} className="h-3" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Heures Admin</span>
                  <span className="font-medium">{formatNumber(production.heures_admin)} hrs ({formatPercent(100 - production.pct_heures_terrain)})</span>
                </div>
                <Progress value={100 - production.pct_heures_terrain} className="h-3 [&>div]:bg-blue-500" />
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-900">Total</span>
                  <span className="font-bold text-slate-900">{formatNumber(totalHeures)} heures</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenu par employé */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Revenu par Employé</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className="bg-emerald-50 rounded-lg p-4 cursor-pointer hover:bg-emerald-100 transition-colors"
                onClick={() => {
                  setSelectedKPI({title: "Revenu par Employé PRQC", kpiKey: "revenu_employe_prqc", isPercent: false, departement: "PRQC"});
                  setShowTrendModal(true);
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-emerald-600">PRQC (Peinture)</p>
                    <p className="text-xs text-emerald-500">{nbEmployesPRQC} employés</p>
                  </div>
                  <span className="kpi-value text-xl text-emerald-700">{formatCurrency(revenuParEmployePRQC)}</span>
                </div>
              </div>
              <div 
                className="bg-blue-50 rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => {
                  setSelectedKPI({title: "Revenu par Employé LMDS", kpiKey: "revenu_employe_lmds", isPercent: false, departement: "LMDS"});
                  setShowTrendModal(true);
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-blue-600">LMDS (Stores)</p>
                    <p className="text-xs text-blue-500">{nbEmployesLMDS} sous-traitants</p>
                  </div>
                  <span className="kpi-value text-xl text-blue-700">{formatCurrency(revenuParEmployeLMDS)}</span>
                </div>
              </div>
              <div 
                className="bg-slate-100 rounded-lg p-4 cursor-pointer hover:bg-slate-200 transition-colors"
                onClick={() => {
                  setSelectedKPI({title: "Revenu par Employé Combiné", kpiKey: "revenu_employe_combine", isPercent: false, departement: selectedDept});
                  setShowTrendModal(true);
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600">Combiné</p>
                    <p className="text-xs text-slate-500">{nbEmployesPRQC + nbEmployesLMDS} personnes</p>
                  </div>
                  <span className="kpi-value text-xl text-slate-700">{formatCurrency(revenuParEmployeCombine)}</span>
                </div>
              </div>
              <div className="text-xs text-slate-400 text-center">
                Cible: {formatCurrency(8333)} / employé / mois
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ratios de coûts */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Ratios de Coûts (PRQC)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div 
              className="cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors"
              onClick={() => {
                setSelectedKPI({title: "Coût MO / Revenus", kpiKey: "cout_mo_revenus", isPercent: true, departement: "PRQC"});
                setShowTrendModal(true);
              }}
            >
              <IndicatorRow 
                label="Coût MO / Revenus" 
                value="54.75" 
                unit="%" 
                targetConfig={prodTargets.cout_mo_revenus}
                description={prodTargets.cout_mo_revenus?.description}
              />
            </div>
            <IndicatorRow 
              label="Matériaux + Peinture / Revenus" 
              value="15" 
              unit="%" 
              targetConfig={prodTargets.materiaux_revenus}
              description={prodTargets.materiaux_revenus?.description}
            />
          </div>
        </CardContent>
      </Card>

      {/* Modal Tendance */}
      {showTrendModal && selectedKPI && (
        <TrendModal
          open={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title={selectedKPI.title}
          kpiKey={selectedKPI.kpiKey}
          departement={selectedKPI.departement}
          isPercent={selectedKPI.isPercent}
          chartType={selectedKPI.isPercent ? "line" : "bar"}
        />
      )}

      {/* Équipes PRQC */}
      {equipes && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
          {/* Teams PRQC */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#1a237e]" />
                Équipes PRQC — {MONTHS[month]} {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {equipes.equipes_prqc.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-6">Aucun chantier ce mois</p>
              ) : (
                <div className="space-y-3">
                  {equipes.equipes_prqc.map(team => (
                    <div key={team.chef_nom} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium text-slate-900">{team.chef_nom}</p>
                        <p className="text-xs text-slate-500">{team.nb_projets} projet{team.nb_projets > 1 ? "s" : ""} · {formatNumber(team.heures)} h</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#1a237e]">{formatCurrency(team.ca)}</p>
                        <p className="text-xs text-slate-400">CA</p>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 flex justify-between text-sm font-semibold border-t border-slate-200">
                    <span>Total PRQC</span>
                    <span className="text-[#1a237e]">{formatCurrency(equipes.total_ca_prqc)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* LMDS subcontractors */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Store className="w-5 h-5 text-emerald-600" />
                Sous-traitants LMDS — {MONTHS[month]} {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {equipes.sous_traitants_lmds.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-6">Aucun rapport ce mois</p>
              ) : (
                <div className="space-y-3">
                  {equipes.sous_traitants_lmds.map(st => (
                    <div key={st.installer_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium text-slate-900">{st.nom}</p>
                        <p className="text-xs text-slate-500">{st.nb_lignes} installation{st.nb_lignes > 1 ? "s" : ""}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{formatCurrency(st.ca)}</p>
                        <p className="text-xs text-slate-400">CA magasin</p>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 flex justify-between text-sm font-semibold border-t border-slate-200">
                    <span>Total LMDS</span>
                    <span className="text-emerald-600">{formatCurrency(equipes.total_ca_lmds)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Section Analyses (propriétaires seulement) ────────────────────── */}
      {canVoirAnalysesFiab && (
        <div className="mt-8" data-testid="section-analyses-production">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Analyses — Fiabilite des equipes
          </h2>
          {/* 🚀 [Phase v13.52 — 2026-07-16] KPI Coût employeur moyen pondéré */}
          <div className="mb-4">
            <KpiCoutEmployeurMoyenPondere />
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Score base sur l'historique des chantiers termines : respect des delais, efficacite des heures, et tendance.
          </p>
          <ScoreFiabiliteChefs />
        </div>
      )}
    </div>
  );
};

export default Production;
